import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr'
import { arrayToEntityDict, userSlimId, type UserCoursePerformance, type UserSlim } from '@webapp2/shared/model'
import { updateByTypes as adminUpdateByTypes, type AdminEntityType } from '../admin/services/admin-entity-service'
import { ensureUsersForMultiplayerSessions } from '../admin/services/admin-user-service'
import { adminState } from '../admin/state'
import { config } from '../main'
import { state, type MultiplayerSession } from '../state'
import { toCamelCase } from '../utils/text'
import { get, postGetBody } from './api'
import { cache } from './cache'
import { updateByTypes, type EntityType } from './entity-service'

export async function initialize() {
  if (navigator.webdriver) {
    window.testSignalRHandler = handleMessage
    return
  }

  const connection = new HubConnectionBuilder()
    .withUrl(`${config.signalrUrl}/client/?hub=ossovr`, {
      accessTokenFactory: () => getAccessToken()
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Information)
    .build()

  connection.on('loop', handleMessage)
  connection.on('message', () => {}) //no-op to stop warning for legacy signalr messagess
  connection.onclose(() => {
    console.log('signalr connection closed, restarting')
    setTimeout(async () => {
      if (connection.state !== HubConnectionState.Disconnected) return
      await connection.start()
    }, 5000)
  })
  await connection.start()
}

async function getAccessToken() {
  const response = await get<{ token: string }>('signalr-token')
  if (!response || !response.body) {
    throw new Error('failed to get signalr token')
  }
  return response.body.token
}

async function handleMessage<T extends MessageType>(type: T, message: MessagePayloads[T]) {
  console.log('signalr message', type, message)
  const handler = handlerDictionary[type] as (payload: MessagePayloads[T]) => Promise<void>
  if (handler) {
    await handler(message)
  }
}

async function handleEntitiesUpdated(types: EntityType[]) {
  // TODO: fix type casing across webapp2 - should be Pascal
  types = types.map(x => toCamelCase(x)) as EntityType[]
  await updateByTypes(types)
}

async function handleAdminEntitiesSlimUpdated(types: string[]) {
  // TODO: fix type casing across webapp2 - should be Pascal
  const adminEntityTypes = types.map(x => x + 'Slim') as AdminEntityType[]
  await adminUpdateByTypes(adminEntityTypes)
}

function handleAdminEntitiesUpdatedWithIds(entitiesUpdated: EntitiesUpdated[]) {
  const tasks = entitiesUpdated.map(async ({ type, ids }) => {
    if (type === 'MultiplayerSession') {
      const idsToLoad = Object.keys(adminState.multiplayerSessions).filter(x => ids.includes(x))
      if (idsToLoad.length === 0) {
        return
      }
      const response = await postGetBody<MultiplayerSession[]>('admin/documents-by-id', {
        type: 'MultiplayerSession',
        ids: idsToLoad
      })
      if (response) {
        adminState.multiplayerSessions = { ...adminState.multiplayerSessions, ...arrayToEntityDict(response) }
        await ensureUsersForMultiplayerSessions(response)
      }
    } else if (type === 'User') {
      const response = await postGetBody<UserSlim[]>('admin/documents-by-id', {
        type: 'UserSlim',
        ids: ids.map(userSlimId)
      })
      if (response) {
        adminState.userSlims = { ...adminState.userSlims, ...arrayToEntityDict(response) }
      }
    }
  })
  return Promise.all(tasks)
}

async function handleUserCoursePerformanceUpdated(userCoursePerformance: UserCoursePerformance) {
  await cache.set(userCoursePerformance.id, userCoursePerformance)
  state.userCoursePerformances[userCoursePerformance.id] = userCoursePerformance
}

const handlerDictionary = {
  entitiesUpdated: handleEntitiesUpdated,
  adminEntitiesSlimUpdated: handleAdminEntitiesSlimUpdated,
  adminEntitiesUpdated: adminUpdateByTypes,
  adminEntitiesUpdatedWithIds: handleAdminEntitiesUpdatedWithIds,
  userCoursePerformanceUpdated: handleUserCoursePerformanceUpdated
} as const

type MessageType = keyof typeof handlerDictionary

type MessagePayloads = {
  [K in MessageType]: Parameters<(typeof handlerDictionary)[K]>[0]
}

type EntitiesUpdated = {
  type: string
  ids: string[]
}
